import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Button,
  TextField,
  Stack,
  Tooltip,
  Divider,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtMoney = (val, opts = {}) => {
  if (val == null || val === '') return '—';
  const num = parseFloat(val);
  if (isNaN(num)) return '—';
  const cents = opts.cents ?? false;
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  });
};

const fmtDate = (v) => {
  if (!v) return '';
  try { return new Date(v).toISOString().slice(0, 10); } catch (_) { return String(v); }
};

const firstOfMonthIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

// ─── Component ────────────────────────────────────────────────────────────────

const MTDReconciler = () => {
  const { user } = useAuth();

  // Role gate — Management + Finance only. Duplicates the server-side check
  // so the UI fails fast with a clear message if anyone else lands here.
  const hasAccess = (() => {
    if (!user) return false;
    if (user.role === 'Management' || user.role === 'Finance') return true;
    if (Array.isArray(user.roles)) {
      return user.roles.includes('Management') || user.roles.includes('Finance');
    }
    return false;
  })();

  const [fromDate, setFromDate] = useState(firstOfMonthIso());
  const [toDate, setToDate]     = useState(todayIso());
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [data, setData]         = useState(null);
  const [downloading, setDownloading] = useState(false);

  const loadPreview = useCallback(async () => {
    if (!hasAccess) return;
    setLoading(true);
    setError(null);
    try {
      // api baseURL already includes /api — do not double-prefix.
      const resp = await api.get(
        `/ar/payments/detail?from=${fromDate}&to=${toDate}`
      );
      // Guard: if a routing/proxy mistake returns HTML (e.g. the SPA catch-all),
      // axios hands us a string body. Surface this as an error rather than
      // silently showing "No transactions".
      if (typeof resp.data === 'string' || !Array.isArray(resp.data?.rows)) {
        throw new Error('Unexpected response from server (expected JSON with rows[]). Contact Tom Clancy.');
      }
      setData(resp.data);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || e.message || 'Failed to load detail');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, hasAccess]);

  useEffect(() => {
    loadPreview();
    // Only auto-run on first mount and explicit button clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadCsv = async () => {
    setDownloading(true);
    try {
      const resp = await api.get(
        `/ar/payments/detail?from=${fromDate}&to=${toDate}&format=csv`,
        { responseType: 'blob' }
      );
      const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mtd-cash-received-${fromDate}_to_${toDate}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(`Download failed: ${e?.response?.data?.error || e.message}`);
    } finally {
      setDownloading(false);
    }
  };

  // Quick-pick helpers
  const setThisMonth = () => {
    setFromDate(firstOfMonthIso());
    setToDate(todayIso());
  };
  const setLastMonth = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = d.getMonth(); // 0-based; previous month = m - 1, handle January
    const prev = new Date(y, m - 1, 1);
    const start = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}-01`;
    const last = new Date(y, m, 0); // day 0 of current month = last day of prev month
    const end = `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
    setFromDate(start);
    setToDate(end);
  };
  const setYTD = () => {
    const d = new Date();
    setFromDate(`${d.getFullYear()}-01-01`);
    setToDate(todayIso());
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (!hasAccess) {
    return (
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Access restricted to Management and Finance users.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Contact Tom Clancy if you believe you should have access to this page.
        </Typography>
      </Box>
    );
  }

  const rows = data?.rows || [];
  const previewLimit = 50;
  const previewRows = rows.slice(0, previewLimit);
  const netTotal = data?.net_total ?? 0;
  const rowCount = data?.row_count ?? 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderLeft: '4px solid #2e7d32' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <AccountBalanceIcon sx={{ fontSize: 32, color: 'success.main' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight="bold">MTD Cash Received Reconciler</Typography>
            <Typography variant="body2" color="text.secondary">
              Row-level deposit-date detail from Sage, column-matched to the Excel ODBC query.
              Use this to reconcile Command Center's MTD Collected figure against Pat's Excel pull.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Controls */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip
              icon={<CalendarTodayIcon />}
              label="This Month"
              size="small"
              variant="outlined"
              onClick={setThisMonth}
              clickable
            />
            <Chip
              icon={<CalendarTodayIcon />}
              label="Last Month"
              size="small"
              variant="outlined"
              onClick={setLastMonth}
              clickable
            />
            <Chip
              icon={<CalendarTodayIcon />}
              label="YTD"
              size="small"
              variant="outlined"
              onClick={setYTD}
              clickable
            />
          </Box>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadPreview}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Run'}
          </Button>
          <Tooltip title="Download all rows as CSV — opens in Excel, lines up with Pat's query">
            <span>
              <Button
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={handleDownloadCsv}
                disabled={downloading || !data || rowCount === 0}
              >
                {downloading ? 'Preparing…' : 'Download CSV'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary cards */}
      {data && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Net Cash Received
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main" sx={{ mt: 0.5 }}>
                  {fmtMoney(netTotal, { cents: true })}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  SUM of −Amount across all rows
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Transaction Rows
                </Typography>
                <Typography variant="h4" fontWeight="bold" sx={{ mt: 0.5 }}>
                  {rowCount.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {data?.window?.from} → {data?.window?.to}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Distinct Customers
                </Typography>
                <Typography variant="h4" fontWeight="bold" sx={{ mt: 0.5 }}>
                  {new Set(rows.map(r => r.Customer)).size.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Unique AR codes in window
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Preview table */}
      <Paper elevation={1} sx={{ overflow: 'hidden' }}>
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Preview {rowCount > previewLimit ? `(first ${previewLimit} of ${rowCount.toLocaleString()} rows)` : `(${rowCount.toLocaleString()} rows)`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Columns match Pat's Excel ODBC query exactly
          </Typography>
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {data ? 'No transactions in this date range.' : 'Pick a date range and click Run.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem', bgcolor: '#f5f5f5' } }}>
                  <TableCell>Customer</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Deposit Date</TableCell>
                  <TableCell>Transaction Date</TableCell>
                  <TableCell>Dep Item Type</TableCell>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Job</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRows.map((r, i) => (
                  <TableRow key={`${r.Customer}-${r.Invoice}-${i}`} hover>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.Customer}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{r.Name}</Typography></TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{fmtDate(r.Deposit_Date)}</Typography></TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{fmtDate(r.Transaction_Date)}</Typography></TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{r.Dep_Item_Type || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{r.Invoice || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{r.Job || '—'}</Typography></TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={Number(r.Amount) < 0 ? 'success.main' : 'error.main'}
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {fmtMoney(r.Amount, { cents: true })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Rule footer */}
      {data && (
        <Paper elevation={0} sx={{ mt: 2, p: 1.5, bgcolor: '#f9f9f9', borderLeft: '3px solid #bdbdbd' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            <strong>Rule:</strong> {data.rule || 'Deposit_Date BETWEEN from AND to — no Transaction_Type filter'}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default MTDReconciler;
